import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import baseStyles from './styles/outline-internal-hero.base.pcss?inline';
import globalStyles from './styles/outline-internal-hero.global.pcss?inline';
import '../outline-container/outline-container';
import '../outline-icon/outline-icon';
import '../outline-cta/outline-cta';
import { Loader } from '@googlemaps/js-api-loader';
import { Coords } from '../../utilities/types';
import { SlotHelper } from '../../controllers/slot-helper';

export const internalThemes = ['light', 'dark'] as const;
export type InternalTheme = (typeof internalThemes)[number];

const componentName = 'outline-internal-hero';

@customElement('outline-internal-hero')
export class OutlineInternalHero extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: baseStyles,
  });

  private _slotHelper = new SlotHelper(this);

  @property({ type: String })
  theme: InternalTheme;

  @property({ type: String })
  address: string;

  @property({ type: String })
  directionsURL: string;

  @property({ type: String })
  drive: string;

  @property({ type: String })
  distance: string;

  @queryAssignedElements({ slot: 'location-address-first' })
  line1Ref: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'location-address-second' })
  line2Ref: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'location-address-third' })
  line3Ref: Array<HTMLElement>;

  @property({ type: Boolean, reflect: true })
  hasSecondaryContent: boolean;

  @property({ type: Boolean, reflect: true })
  hasCTAContent: boolean;

  @property({ type: Boolean, reflect: true })
  isLocation: boolean;

  @property({ type: Boolean, reflect: true })
  hasPhone: boolean;

  @property({ type: Boolean, reflect: true })
  hasFax: boolean;

  @property({ type: Boolean, reflect: true })
  hasEntrance: boolean;

  @property({ type: Boolean, reflect: true })
  hasHours: boolean;

  private _currentLocation: Coords;

  connectedCallback(): void {
    super.connectedCallback();
  }

  async firstUpdated() {
    if (this.isLocation) {
      await this.getAddress();
      await this.ensureGoogleMapsLoaded();
      this.getLocationInfo();
      this.getAddressLocationData();
    }
    this.hasSecondaryContent = this._slotHelper.isNotEmpty('secondary');
    this.hasCTAContent = this._slotHelper.isNotEmpty('cta');
    this.secondaryContent();
  }

  render(): TemplateResult {
    return html`
      <div class="outline-internal-hero">
        <outline-container
          content-max-width="large"
          x-padding="space-0"
          y-padding="space-0"
        >
          <div class="outline-internal-hero--content">
            <div class="outline-internal-hero--main">
              <div class="eyebrow-wrapper">
                <slot name="eyebrow"></slot>
              </div>
              <div class="heading-wrapper">
                <slot name="heading"></slot>
              </div>
              <div class="content-wrapper">
                <slot name="content"></slot>
              </div>
              ${!this.isLocation ? null : this.locationContentTemplate()}
              ${!this.hasCTAContent
                ? null
                : html`<div class="cta-wrapper"><slot name="cta"></slot></div>`}
            </div>
            ${this.secondaryContent()}
          </div>
        </outline-container>
      </div>
    `;
  }

  locationContentTemplate() {
    this.hasPhone = this._slotHelper.isNotEmpty('location-phone');
    this.hasFax = this._slotHelper.isNotEmpty('location-fax');
    this.hasEntrance = this._slotHelper.isNotEmpty('location-entrance');
    this.hasHours = this._slotHelper.isNotEmpty('location-hours');

    return html`
      <div class="outline-internal-hero--location">
        <div class="location-info">
          ${this.locationEntranceTemplate()}
          <div class="location-address">
            ${this.addressLine1Template()} ${this.addressLine2Template()}
            ${this.addressLine3Template()}
            <span class="address-line line-2">
              <slot name="location-address-second"></slot>
            </span>
            ${this.directionsURL
              ? html`<span class="address-line directions">
                  <a
                    class="directions-link"
                    href="${this.directionsURL}"
                    target="_blank"
                    >Get directions</a
                  >
                </span>`
              : null}
            <span class="address-line directions">
              ${this.generateDirectionsLink()}
            </span>
          </div>
          ${this.drive || this.distance
            ? html` <div class="location-drive">
                <div class="info-line drive-time">
                  <span>${this.distance}</span>
                  <span>${this.drive} ETA</span>
                </div>
              </div>`
            : null}
        </div>
        <div class="location-hours-contact">
          ${this.locationHourTemplate()}
          <div class="location-contact">
            ${this.locationPhoneTemplate()} ${this.locationFaxTemplate()}
          </div>
        </div>
      </div>
    `;
  }

  secondaryContent() {
    if (!this.hasSecondaryContent) {
      return;
    }
    const slottedElement = this.querySelector(
      '[slot="secondary"]'
    ) as HTMLElement;
    if (
      slottedElement?.nodeName === 'PICTURE' ||
      slottedElement?.nodeName === 'IMG'
    ) {
      return html`<div class="outline-internal-hero--secondary--image">
        <slot name="secondary"></slot>
      </div>`;
    }
    return html`<div class="outline-internal-hero--secondary--text">
      <slot name="secondary"></slot>
      <slot name="secondary-description"></slot>
      <slot name="secondary-cta1"></slot>
      <slot name="secondary-cta2"></slot>
    </div>`;
  }

  addressLine1Template() {
    if (this._slotHelper.isNotEmpty('location-address-first')) {
      return html`<span class="address-line line-1"
        ><slot name="location-address-first"></slot
      ></span>`;
    }
  }
  addressLine2Template() {
    if (this._slotHelper.isNotEmpty('location-address-second')) {
      return html`<span class="address-line line-2"
        ><slot name="location-address-second"></slot
      ></span>`;
    }
  }
  addressLine3Template() {
    if (this._slotHelper.isNotEmpty('location-address-third')) {
      return html`<span class="address-line line-3"
        ><slot name="location-address-third"></slot
      ></span>`;
    }
  }

  locationPhoneTemplate() {
    return this.hasPhone
      ? html`<div class="info-line phone">
          <slot name="location-phone"></slot>
        </div>`
      : null;
  }

  locationFaxTemplate() {
    return this.hasFax
      ? html`<div class="info-line fax"><slot name="location-fax"></slot></div>`
      : null;
  }

  locationEntranceTemplate() {
    return this.hasEntrance
      ? html`<div class="location-title">
          <slot name="location-entrance"></slot>
        </div>`
      : null;
  }

  locationHourTemplate() {
    return this.hasHours
      ? html` <div class="location-hours">
          <span class="location-hours--label">Hours</span>
          <slot name="location-hours"></slot>
        </div>`
      : null;
  }

  getLocationInfo() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          this._currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        },
        () => {
          console.log('Error retrieving location information.');
        }
      );
    } else {
      console.log('Your browser does not support geolocation.');
    }
  }

  async getAddressLocationData() {
    const geocoder = new google.maps.Geocoder();
    await geocoder
      .geocode({ address: `${this.address}` })
      .then(res => {
        if (res.results[0]) {
          const { lat, lng } = res.results[0].geometry.location.toJSON();
          this.generateDriveTime({ lat, lng });
        }
      })
      .catch(err => console.log(err));
  }

  generateDriveTime(addressLocation: Coords) {
    const directionsService = new google.maps.DirectionsService();
    if (this._currentLocation?.lat && this._currentLocation?.lng) {
      directionsService
        .route({
          origin: this._currentLocation,
          destination: addressLocation,
          travelMode: google.maps.TravelMode.DRIVING,
        })
        .then(response => {
          this.drive = response.routes[0].legs[0].duration?.text;
          this.distance = response.routes[0].legs[0].distance?.text;
        })
        .catch(error => console.error('Directions error:', error));
    }
  }

  generateDirectionsLink() {
    const urlPrefix = 'https://www.google.com/maps/dir/?api=1&destination=';
    const cleaned = this.address?.replace(/\s+/g, '%20').replace(/\n/g, '');
    this.directionsURL = `${urlPrefix}${cleaned}`;
  }

  async getAddress() {
    if (!this.address) {
      this.address =
        `${this.line1Ref[0]?.innerHTML ?? ''} ${this.line3Ref[0]?.innerHTML ?? ''}`.trim();
    }
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (window.google?.maps?.places?.AutocompleteService) return;

    if (!(window as any)._googleMapsPromise) {
      const loader = new Loader({
        apiKey: import.meta.env.VITE_DEVELOPMENT_GOOGLE_MAP_API_KEY,
        version: 'weekly',
        libraries: ['places'],
      });
      (window as any)._googleMapsPromise = loader.load();
    }

    await (window as any)._googleMapsPromise;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineInternalHero;
  }
}
